/**
 * Onboarding documents for an apartment's Contract section (COL-225, item 5).
 *
 * Documents signed or produced during the owner onboarding flow
 * (owner_onboarding_submissions) are surfaced as attachments on the apartment
 * Contract tab. Submissions link to a property via their token
 * (owner_onboarding_tokens.property_id); we match on the apartment's property,
 * and — when the apartment has a linked owner with an email — prefer the
 * submission(s) for that owner.
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type OnboardingDoc = {
  /** Stable key for React lists. */
  key: string;
  /** Human label, e.g. "Signed contract". */
  label: string;
  /** Public/download URL. */
  url: string;
  /** Owner name from the submission. */
  ownerName: string;
  /** ISO timestamp the submission was made. */
  submittedAt: string | null;
};

type SubmissionRow = {
  id: string;
  name: string;
  email: string;
  status: string | null;
  submitted_at: string | null;
  signed_contract_url: string | null;
  service_sheet_en_url: string | null;
  service_sheet_de_url: string | null;
  token: { property_id: number | string | null; email: string | null } | null;
};

const DOC_FIELDS: Array<{ field: keyof SubmissionRow; label: string }> = [
  { field: "signed_contract_url", label: "Signed management contract" },
  { field: "service_sheet_en_url", label: "Service sheet (EN)" },
  { field: "service_sheet_de_url", label: "Service sheet (DE)" },
];

/**
 * Fetch onboarding documents tied to a property, optionally narrowed to a
 * specific owner email. Returns a flat, render-ready document list.
 */
export function useOwnerOnboardingDocs(
  propertyId: number | null | undefined,
  ownerEmail?: string | null
) {
  return useQuery({
    queryKey: ["v2", "onboarding-docs", propertyId ?? null, ownerEmail ?? null],
    enabled: !!propertyId,
    queryFn: async (): Promise<OnboardingDoc[]> => {
      if (!propertyId) return [];
      const { data, error } = await supabase
        .from("owner_onboarding_submissions")
        .select(
          "id, name, email, status, submitted_at, signed_contract_url, service_sheet_en_url, service_sheet_de_url, token:token_id!inner(property_id, email)"
        )
        .eq("token.property_id", propertyId)
        .order("submitted_at", { ascending: false });
      if (error) throw error;

      let rows = (data ?? []) as SubmissionRow[];

      // When the apartment has a linked owner email and at least one submission
      // matches it, prefer those — otherwise fall back to all property
      // submissions (better to show something than nothing).
      if (ownerEmail) {
        const lower = ownerEmail.trim().toLowerCase();
        const matched = rows.filter(
          (r) =>
            r.email?.toLowerCase() === lower ||
            r.token?.email?.toLowerCase() === lower
        );
        if (matched.length > 0) rows = matched;
      }

      const docs: OnboardingDoc[] = [];
      for (const r of rows) {
        for (const { field, label } of DOC_FIELDS) {
          const url = r[field] as string | null;
          if (url) {
            docs.push({
              key: `${r.id}-${String(field)}`,
              label,
              url,
              ownerName: r.name,
              submittedAt: r.submitted_at,
            });
          }
        }
      }
      return docs;
    },
  });
}
